/**
 * DotSphereSpinner — VaultSentinel's animated AI mark.
 *
 * Renders the branded Lottie animation (After Effects / bodymovin export in
 * ./ai-logo.json) — this replaced the old hand-built canvas dot-sphere. The name
 * and the `size`/`label` props are kept so existing call sites keep working
 * unchanged, e.g. `<DotSphereSpinner size={48} />`. The legacy `logoSrc`,
 * `variant` and `tweaks` props are still accepted but no longer used.
 *
 * Accessibility:
 *   - Wrapped in role="status" / aria-live="polite" with an announced label.
 *   - Respects prefers-reduced-motion: the animation holds on its first frame
 *     rather than looping.
 */

import * as React from 'react';
import Lottie from 'lottie-react';
import aiLogoAnimation from './ai-logo.json';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

export interface DotSphereSpinnerProps {
  /** Pixel size of the (square) mark. */
  size?: number;
  /** Accessible label announced via aria-live. */
  label?: string;
  className?: string;
  style?: React.CSSProperties;
  /** @deprecated no longer used — kept so existing call sites don't need editing. */
  logoSrc?: string;
  /** @deprecated no longer used — kept so existing call sites don't need editing. */
  variant?: 'dark' | 'light';
  /** @deprecated no longer used — kept so existing call sites don't need editing. */
  tweaks?: unknown;
}

const srOnly: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

export function DotSphereSpinner({
  size = 64,
  label = 'Loading',
  className,
  style,
}: DotSphereSpinnerProps) {
  const reducedMotion = usePrefersReducedMotion();
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={label}
      className={className}
      style={{
        position: 'relative',
        width: size,
        height: size,
        display: 'inline-block',
        lineHeight: 0,
        ...style,
      }}
    >
      <Lottie
        animationData={aiLogoAnimation}
        loop={!reducedMotion}
        autoplay={!reducedMotion}
        style={{ width: '100%', height: '100%' }}
        rendererSettings={{ preserveAspectRatio: 'xMidYMid meet' }}
      />
      <span style={srOnly}>{label}</span>
    </div>
  );
}

export default DotSphereSpinner;
